// src/pages/User/components/UserBranchManager.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserBranchPreferencesThunk,
  updateUserPrimaryBranchThunk,
  getUserVisitHistoryThunk,
} from "../../../store/thunks/userThunks";
import { getBranchesThunk } from "../../../store/thunks/branchThunks";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Badge from "../../../components/common/Badge";
import {
  LocationMarkerIcon,
  OfficeBuildingIcon,
  ClockIcon,
  TrendingUpIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationIcon,
  SwitchHorizontalIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../../utils/notification";

const UserBranchManager = ({ isOpen, onClose, userId, userName }) => {
  const dispatch = useDispatch();
  const { branchData, loadingBranch } = useSelector((state) => state.user);
  const { branches } = useSelector((state) => state.branch);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isChangingBranch, setIsChangingBranch] = useState(false);
  const [activeTab, setActiveTab] = useState("current");

  useEffect(() => {
    if (isOpen && userId) {
      dispatch(getUserBranchPreferencesThunk(userId));
      dispatch(getUserVisitHistoryThunk({ userId }));
      dispatch(getBranchesThunk({ isActive: true }));
    }
  }, [isOpen, userId, dispatch]);

  const handleChangeBranch = async () => {
    if (!selectedBranch) {
      notifyError("Please select a branch");
      return;
    }

    setIsChangingBranch(true);
    try {
      const action = await dispatch(
        updateUserPrimaryBranchThunk({
          userId,
          branchData: {
            branchId: selectedBranch._id,
            branchCode: selectedBranch.code,
            branchName: selectedBranch.name,
          },
        })
      );

      if (updateUserPrimaryBranchThunk.fulfilled.match(action)) {
        notifySuccess("Primary branch updated successfully");
        dispatch(getUserBranchPreferencesThunk(userId));
        setSelectedBranch(null);
      } else {
        notifyError(action.payload || "Failed to update branch");
      }
    } finally {
      setIsChangingBranch(false);
    }
  };

  const { branchPreferences, visitHistory } = branchData;
  const primaryBranch = branchPreferences?.primaryBranch;

  const tabs = [
    { id: "current", label: "Current Branch", icon: OfficeBuildingIcon },
    { id: "history", label: "Visit History", icon: CalendarIcon },
    { id: "change", label: "Change Branch", icon: SwitchHorizontalIcon },
  ];

  // Render Current Branch Tab
  const renderCurrentBranch = () => {
    if (!primaryBranch) {
      return (
        <div className="text-center py-12">
          <LocationMarkerIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No primary branch assigned</p>
          <Button variant="primary" onClick={() => setActiveTab("change")}>
            Assign Primary Branch
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Primary Branch Card */}
        <div className="bg-gradient-to-br from-primary-light to-primary/20 p-6 rounded-lg border-2 border-primary">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="success" size="lg">
                  Primary Branch
                </Badge>
                {primaryBranch.isAutoSelected && (
                  <Badge variant="info" size="sm">
                    Auto-Selected
                  </Badge>
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900">
                {primaryBranch.branchName}
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                Code: {primaryBranch.branchCode}
              </p>
            </div>
            <OfficeBuildingIcon className="w-12 h-12 text-primary" />
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ClockIcon className="w-4 h-4" />
            <span>
              Selected on:{" "}
              {new Date(primaryBranch.selectedAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Recent Branches */}
        {branchPreferences?.recentBranches?.length > 0 && (
          <div>
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <ClockIcon className="w-5 h-5 text-gray-600" />
              Recently Visited Branches
            </h4>
            <div className="space-y-2">
              {branchPreferences.recentBranches.map((branch, index) => (
                <div
                  key={branch.branchCode}
                  className="bg-white border border-gray-200 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {branch.branchName}
                      </p>
                      <p className="text-sm text-gray-600">
                        {branch.branchCode}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">
                        {branch.visitCount} visit
                        {branch.visitCount !== 1 ? "s" : ""}
                      </p>
                      <p className="text-xs text-gray-500">
                        Last:{" "}
                        {new Date(branch.lastVisited).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Location Consent Status */}
        {branchPreferences?.locationConsent && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <LocationMarkerIcon className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-blue-900">Location Access</p>
                <p className="text-sm text-blue-700">
                  {branchPreferences.locationConsent.granted
                    ? "Location access granted"
                    : "Location access not granted"}
                </p>
                {branchPreferences.locationConsent.lastLocation && (
                  <p className="text-xs text-blue-600 mt-1">
                    Last captured:{" "}
                    {new Date(
                      branchPreferences.locationConsent.lastLocation.capturedAt
                    ).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render Visit History Tab
  const renderVisitHistory = () => {
    if (!visitHistory || visitHistory.length === 0) {
      return (
        <div className="text-center py-12">
          <CalendarIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No visit history available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="font-semibold text-gray-800">
            Total Visits: {visitHistory.length}
          </h4>
        </div>

        <div className="space-y-3">
          {visitHistory.map((visit, index) => (
            <div
              key={index}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-primary transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge
                      variant={
                        visit.visitType === "APPOINTMENT"
                          ? "info"
                          : visit.visitType === "WALK_IN"
                          ? "warning"
                          : "secondary"
                      }
                    >
                      {visit.visitType?.replace("_", " ")}
                    </Badge>
                    {visit.madePurchase && (
                      <Badge variant="success">
                        <CheckCircleIcon className="w-3 h-3 inline mr-1" />
                        Purchased
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    <CalendarIcon className="w-4 h-4 inline mr-1" />
                    {new Date(visit.visitDate).toLocaleString()}
                  </p>
                  {visit.duration && (
                    <p className="text-sm text-gray-600">
                      <ClockIcon className="w-4 h-4 inline mr-1" />
                      Duration: {visit.duration} minutes
                    </p>
                  )}
                  {visit.assistedBy && (
                    <p className="text-sm text-gray-600">
                      Assisted by: {visit.assistedBy}
                    </p>
                  )}
                </div>
                {visit.purchaseValue > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Purchase Amount</p>
                    <p className="text-lg font-bold text-green-600">
                      ₹{visit.purchaseValue.toLocaleString()}
                    </p>
                  </div>
                )}
              </div>

              {visit.adminNotes && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <p className="text-xs text-gray-500">Admin Notes:</p>
                  <p className="text-sm text-gray-700">{visit.adminNotes}</p>
                </div>
              )}

              {visit.requiresFollowUp && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-orange-600">
                    <ExclamationIcon className="w-4 h-4" />
                    <p className="text-sm font-medium">Requires Follow-up</p>
                  </div>
                  {visit.followUpDate && (
                    <p className="text-xs text-gray-600 mt-1">
                      Follow-up date:{" "}
                      {new Date(visit.followUpDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render Change Branch Tab
  const renderChangeBranch = () => {
    const availableBranches = branches.filter(
      (branch) => branch.code !== primaryBranch?.branchCode
    );

    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <ExclamationIcon className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-900">
                Change Primary Branch
              </p>
              <p className="text-sm text-yellow-700">
                Changing the primary branch will update the user's default
                branch preference. This action can be undone at any time.
              </p>
            </div>
          </div>
        </div>

        {primaryBranch && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-2">Current Primary Branch</p>
            <p className="font-semibold text-gray-900">
              {primaryBranch.branchName} ({primaryBranch.branchCode})
            </p>
          </div>
        )}

        <div>
          <h4 className="font-semibold text-gray-800 mb-3">
            Select New Primary Branch
          </h4>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {availableBranches.map((branch) => (
              <div
                key={branch._id}
                onClick={() => setSelectedBranch(branch)}
                className={`border rounded-lg p-4 cursor-pointer transition-all ${
                  selectedBranch?._id === branch._id
                    ? "border-primary bg-primary-light ring-2 ring-primary"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{branch.name}</p>
                    <p className="text-sm text-gray-600">{branch.code}</p>
                    {branch.address && (
                      <p className="text-xs text-gray-500 mt-1">
                        {branch.address.city}, {branch.address.state}
                      </p>
                    )}
                  </div>
                  {selectedBranch?._id === branch._id && (
                    <CheckCircleIcon className="w-6 h-6 text-primary" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedBranch(null);
              setActiveTab("current");
            }}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleChangeBranch}
            isLoading={isChangingBranch}
            disabled={!selectedBranch}
          >
            Update Primary Branch
          </Button>
        </div>
      </div>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-3">
          <OfficeBuildingIcon className="w-6 h-6 text-primary" />
          <span>Branch Management - {userName}</span>
        </div>
      }
      isFullScreen={true}
    >
      {loadingBranch ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading branch information...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <div className="flex space-x-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? "border-primary text-primary bg-primary-light/10"
                      : "border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="max-h-[600px] overflow-y-auto">
            {activeTab === "current" && renderCurrentBranch()}
            {activeTab === "history" && renderVisitHistory()}
            {activeTab === "change" && renderChangeBranch()}
          </div>
        </>
      )}
    </Modal>
  );
};

export default UserBranchManager;
